import React, { useEffect, useState } from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { AlertTriangle } from "lucide-react";
import PullToRefresh from "../../components/mobile/pull-to-refresh";
import { useMobileNavigation } from "../../components/mobile/mobile-navigation-context";
import { useUser } from "../../hooks/use-user";
import { cn } from "../../lib/utils";
import { getUserInitials } from "../../lib/navigation";
import { useDashboardData } from "./use-dashboard-data";
import { getGreeting } from "./mobile/format";
import HeroValueCard from "./mobile/HeroValueCard";
import QuickActions from "./mobile/QuickActions";
import OverviewTiles from "./mobile/OverviewTiles";
import InventoryBreakdownCard from "./mobile/InventoryBreakdownCard";
import TrendsCard from "./mobile/TrendsCard";
import RecentActivityCard from "./mobile/RecentActivityCard";
import MobileDashboardSkeleton from "./mobile/MobileDashboardSkeleton";

const COLLAPSE_AFTER = 64;

const useNow = (intervalMs: number) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
};

const MobileDashboard: React.FC = () => {
  const { query, overview, monthlyTrend, recentByType, error } = useDashboardData();
  const { user } = useUser();
  const { openMenu } = useMobileNavigation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const now = useNow(30_000);

  if (query.isLoading) return <MobileDashboardSkeleton />;

  const firstName = user?.fullName?.trim().split(/\s+/)[0];
  const updatedLabel = query.dataUpdatedAt
    ? now - query.dataUpdatedAt < 60_000
      ? "Updated just now"
      : `Updated ${formatDistanceToNowStrict(query.dataUpdatedAt, { addSuffix: true })}`
    : "";

  const avatarButton = (size: "sm" | "lg") => (
    <button
      type="button"
      onClick={openMenu}
      aria-label="Open menu"
      className={cn(
        "flex shrink-0 items-center justify-center border-0 bg-gray-900 p-0 font-semibold text-white transition-transform active:scale-95",
        size === "lg" ? "h-11 w-11 rounded-2xl text-sm" : "h-8 w-8 rounded-xl text-xs",
      )}
    >
      {getUserInitials(user?.fullName)}
    </button>
  );

  return (
    <div className="relative h-full bg-gray-50">
      <div
        className={cn(
          "absolute inset-x-0 top-0 z-10 border-b border-gray-100 bg-white/90 pt-[env(safe-area-inset-top)] backdrop-blur-md transition-all duration-200",
          isCollapsed ? "opacity-100" : "pointer-events-none -translate-y-2 opacity-0",
        )}
      >
        <div className="flex h-12 items-center justify-between px-4">
          <span className="text-base font-bold font-jakarta text-gray-900">Dashboard</span>
          {avatarButton("sm")}
        </div>
      </div>

      <PullToRefresh
        onRefresh={() => query.refetch()}
        onScroll={(scrollTop) => setIsCollapsed(scrollTop > COLLAPSE_AFTER)}
      >
        <div className="space-y-7 px-4 pb-8 pt-[calc(env(safe-area-inset-top)_+_1.25rem)]">
          <header className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{getGreeting()},</p>
              <h1 className="truncate text-2xl font-bold font-jakarta text-gray-900">
                {firstName || "Welcome back"}
              </h1>
              {updatedLabel && (
                <p className="mt-0.5 text-[11px] text-gray-400">
                  {query.isFetching ? "Refreshing…" : `${updatedLabel} · Pull down to refresh`}
                </p>
              )}
            </div>
            {avatarButton("lg")}
          </header>

          {error && (
            <div className="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-red-700">{error}</p>
                <button
                  type="button"
                  onClick={() => query.refetch()}
                  className="mt-2 rounded-lg border-0 bg-red-600 px-3 py-1.5 text-xs font-semibold text-white active:bg-red-700"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          <HeroValueCard inventory={overview?.inventory} />
          <QuickActions />
          <OverviewTiles summary={overview?.summary} />
          <InventoryBreakdownCard inventory={overview?.inventory} />
          <TrendsCard monthlyTrend={monthlyTrend} />
          <RecentActivityCard
            quotations={recentByType.quotations}
            invoices={recentByType.invoices}
            movements={recentByType.movements}
          />
        </div>
      </PullToRefresh>
    </div>
  );
};

export default MobileDashboard;
